import { useState } from 'react';

import { BackLink } from '@/components/BackLink';
import { Button } from '@/components/Button';
import { Callout } from '@/components/Callout';
import { DateField } from '@/components/DateField';
import { Field } from '@/components/Field';
import { KioskCard } from '@/components/KioskCard';
import { KioskScreen } from '@/components/KioskScreen';
import { Select } from '@/components/Select';
import { StatusBadge } from '@/components/StatusBadge';
import { useTranslation } from '@/i18n/useTranslation';
import { captureCandidateSession, SCREEN, useKioskSession } from '@/lib/kioskSession';
import { sessionCandidateFromNames } from '@/lib/sessionCandidate';
import { saveProfile } from '@/services/candidate';
import { todayIso } from '@/shared/calendar';
import { useFocusOnMount } from '@/shared/hooks/useFocusOnMount';
import { useScrollToFirstError } from '@/shared/hooks/useScrollToFirstError';

import { US_STATES } from '../states';
import type { ProfileDraft, ProfileErrors } from '../types';
import {
  BLOCKED_REASON,
  blockingReason,
  digitsOnly,
  EMPTY_PROFILE,
  rejectionErrors,
  validateProfile,
  visibleErrors,
  without,
} from '../types';
import { BlockedScreen } from './BlockedScreen';
import { ConsentDialog } from './ConsentDialog';
import { YesNo } from './YesNo';

/**
 * The quick-apply profile [KAP-7; Figma `04 · Application (profile)` — 12021:40603 / 40566 / 40530].
 *
 * **This is where a candidate the lookup did NOT find lands**, and until it existed they were sent to the
 * returning candidate's hub instead — a screen from the other flow entirely. The branch is the identity
 * result, by the Operator's ruling of 2026-08-31: found → confirm your details; not found → this.
 *
 * **Eleven fields, and the count is [decision 0043]**, which exists to *remove* steps — work history and a
 * skills multi-select were taken out on purpose. Adding a field here is a change to an approved decision.
 *
 * **Continue does not write. It opens a gate** [decisions 0160, 0161, 0195]:
 *
 *   1. the eleven fields are validated here, and a gap is painted here;
 *   2. `ConsentDialog` — a blocking modal, because ISS ruled the consent off this screen [0160];
 *   3. the gate: consent, 18-or-older and work-authorized read TOGETHER [{@link blockingReason}];
 *   4. all three pass → the Bullhorn candidate is created. Any one fails → its `Blocked` screen, and
 *      **nothing has been written**, which is how [0160]'s *"a decline must leave no candidate behind"* is
 *      satisfied: there is no record to orphan.
 *
 * Until this landed the build was two decisions behind — an inline checkbox with no modal, and
 * `goTo(SCREEN.jobs)` reached unconditionally with both eligibility answers ignored, so a candidate who
 * answered `no` to being 18 applied anyway. [0195] § *Two things this decision does NOT do* names exactly
 * that gap.
 *
 * **Nothing sensitive is collected here** and the screen says so: the SSN and direct deposit are completed
 * on the candidate's own phone, never on a shared kiosk [decision 0050 for the I-9; the same rule].
 */
export function ApplicationScreen() {
  const t = useTranslation();
  const heading = useFocusOnMount<HTMLHeadingElement>();
  const goTo = useKioskSession((state) => state.goTo);
  const factors = useKioskSession((state) => state.factors);
  const candidateId = useKioskSession((state) => state.lookupResult?.candidateId);
  const setCandidateId = useKioskSession((state) => state.setCandidateId);
  const enterSessionCandidate = useKioskSession((state) => state.enterSessionCandidate);
  const endCandidateSession = useKioskSession((state) => state.endCandidateSession);
  const askToExit = useKioskSession((state) => state.askToExit);
  const gate = useKioskSession((state) => state.applicationGate);
  const setGate = useKioskSession((state) => state.setApplicationGate);

  // This screen holds more of a stranger's data than any other in the flow — name, phone, home address —
  // half-typed on a machine in a public lobby. A candidate who walks away mid-form clears it: back to
  // Welcome, session gone [KID-1's clear-on-idle rule, the same one Check-in and Arrival arm].
  const [saving, setSaving] = useState(false);

  /*
   * The last name and email are carried forward from the check-in the candidate has just filled in. Asking
   * for them twice on the next screen is the sort of thing that reads as the machine not listening — the
   * frame says as much under the field: *"Pre-filled from your lookup — edit if needed."*
   */
  const [draft, setDraft] = useState<ProfileDraft>({
    ...EMPTY_PROFILE,
    lastName: factors.lastName,
    email: factors.email,
  });
  /**
   * The fields a refused Continue has told the candidate about — **not** the fields that are wrong.
   *
   * What is drawn is {@link visibleErrors}, re-derived on every render, so a mark disappears the moment the
   * field it names is satisfied. Storing the errors themselves is what made a corrected field stay red
   * until the next Continue [Operator, 2026-09-14]. Sticky per field and never populated before the first
   * Continue: the timing rule is "once told, stay live", not "validate as they type".
   */
  const [marked, setMarked] = useState<ProfileErrors>({});
  /** What the API refused on the last save. Cleared per field when the candidate edits it — see {@link set}. */
  const [refused, setRefused] = useState<ProfileErrors>({});
  // Counts refused Continues, so a second press with the same errors scrolls again. See the hook.
  const [refusals, setRefusals] = useState(0);
  useScrollToFirstError(refusals);
  /**
   * Whether the api REFUSED the last save — a state this screen had no way to be in, and was in anyway.
   *
   * Separate from `errors` because a refusal can name a field this screen does not draw: the lookup factors
   * travel with a create and were typed two screens ago. Then nothing is marked, and this line is the only
   * thing standing between the candidate and a silent loss.
   */
  const [notSaved, setNotSaved] = useState(false);

  const set = <K extends keyof ProfileDraft>(key: K, value: ProfileDraft[K]): void => {
    setDraft((current) => ({ ...current, [key]: value }));
    /*
     * An api refusal is dropped the moment the candidate touches the field, and only then.
     *
     * It cannot be re-evaluated the way a local rule can: the api refused a value this screen had already
     * passed, so re-running `validateProfile` would clear the mark on the render it was set in and the
     * candidate would never see why they were held. Editing the field is the one local fact that says the
     * refusal may no longer apply — and the next save is what actually answers it.
     */
    setRefused((current) => without(current, key));
  };

  /**
   * MARKS on Continue, never before it — and once marked, a field is live [Operator, 2026-09-14].
   *
   * The first half is an earlier round of feedback: a field that turns red while somebody is halfway
   * through typing an email tells them they are wrong before they have finished being right, so nothing is
   * painted until they have asked to move on. The Continue button is never disabled either — a greyed-out
   * primary withholds the reason and leaves the person hunting for it.
   *
   * The second half is this one: after that first refusal the field re-evaluates as they type or select,
   * and its mark goes the moment the field is satisfied rather than at the next Continue. Both halves are
   * the prototype's behaviour and neither survives without the other — see {@link visibleErrors}.
   */
  /**
   * Validates, then opens the consent modal — and stops there. **Continue no longer writes** [0195].
   *
   * The check here is the eleven fields only: something missing or malformed is a gap the candidate fixes on
   * this screen, so it is painted and the gate never opens. A `no` to either eligibility question is NOT
   * caught here — it is a complete answer, and refusing it is the gate's job on a screen of its own [0161].
   */
  function submit(): void {
    if (saving) return;
    const found = validateProfile(draft);
    // UNION, not replacement: a field the candidate has once been told about stays live even after they
    // fix it, so blanking it again re-marks it in place instead of waiting for another refused Continue.
    setMarked((current) => ({ ...current, ...found }));
    // A fresh attempt drops the last save's refusals — the write that follows is what answers them now.
    setRefused({});
    setNotSaved(false);
    if (Object.keys(found).length > 0) {
      setRefusals((n) => n + 1);
      return;
    }
    setGate({ stage: 'consent' });
  }

  /**
   * The gate, and then the one write [decision 0195].
   *
   * All three answers are read together the moment the modal is answered. Any refusal lands on its own
   * `Blocked` screen with **nothing written**; only the clean pass reaches {@link createCandidate}, which is
   * the single call site 0195 asks for — *"a build that scatters the create across branches will pass each
   * refusal test and still leak a record on some fourth path"*.
   */
  async function answerConsent(consented: boolean): Promise<void> {
    if (saving) return;
    const reason = blockingReason(draft, consented);
    if (reason !== undefined) {
      setGate({ stage: 'blocked', reason });
      return;
    }
    /*
     * The modal closes BEFORE the write, not after it.
     *
     * A refused write paints its message on the form beneath — the summary line and the field borders — and
     * a modal still standing over them would leave the candidate looking at a consent they had just agreed
     * to while the reason they were stuck sat behind it. The cost is that a retry after a 400 re-opens the
     * modal, which is the honest reading of 0195 anyway: the sequence is Continue → modal → gate → write,
     * and the write that happens is the one this consent was given for.
     */
    setGate(undefined);
    await createCandidate();
  }

  /**
   * Creates the record — the only place in this app that does.
   *
   * **The write is the point of this screen** [decision 0033]: the record is created the moment the page is
   * finished, before a job is picked, so somebody who fills this in and walks away still exists. Until this
   * landed the page collected eleven fields and wrote none of them, and a returning candidate was asked for
   * the same details again because there was nothing to find. [0195] narrows *when* rather than *whether*:
   * "the profile page is finished" now means after the modal and the three-check gate. Early still means
   * early — before any job is picked — it just no longer means before consent.
   *
   * **An unreachable api does not hold anybody** [BR-K7]. They continue to the openings with no candidate
   * id, which is the state the job detail already handles: no submission is attempted, and the gap is the
   * operator's to see rather than a reason to stop a person at a machine.
   *
   * **A REFUSED write does hold them**, and the difference is whether there is anything they can do about
   * it. This screen validates first, so a 400 means the api and this form disagree about a rule — and
   * walking on from that told the candidate their profile was saved when no record existed. Refusing to
   * pretend is not a dead end: they are on a screen with a message and a Continue that works once the value
   * does [the same reading `us-kpw-1` states — *"the candidate is not dead-ended, but the system does not
   * pretend"*].
   */
  async function createCandidate(): Promise<void> {
    setSaving(true);
    /*
     * ⭐ **The profile's submit is a name ENTERED** [03-us-kad-6j-f AC-1a]: *First name* and the first letter
     * of *Last name*, for the live socket's `session.started`. At the submit rather than on the write's
     * answer — the candidate has given their name either way, and a refused write keeps them on this screen.
     */
    enterSessionCandidate(sessionCandidateFromNames(draft.firstName, draft.lastName));
    const stillTheirs = captureCandidateSession();
    const outcome = await saveProfile({
      firstName: draft.firstName.trim(),
      lastName: draft.lastName.trim(),
      email: draft.email.trim(),
      phone: draft.phone.trim(),
      city: draft.city.trim(),
      state: draft.state,
      zip: draft.zip.trim(),
      payRate: draft.payRate.trim(),
      startDate: draft.startDate,

      /*
       * Always `true`, and structurally so [0195].
       *
       * This is reached only through the gate, and the gate's first branch is a decline — so there is no
       * path to this line on which the candidate did not agree. The api's own contract says the same from
       * the other side (*"sent only when TRUE — the screen cannot produce a false"*), and it used to be
       * `draft.consent`: a checkbox the form no longer draws, whose value the modal now carries.
       *
       * It is the permission to call, text or email them, so it is the one with legal weight, and until
       * 2026-09-02 it was never stored at all — the candidate agreed and the agreement died with the
       * session.
       */
      consent: true,
      // Present only if the lookup matched somebody: it makes this an update, never a second record.
      ...(candidateId === undefined
        ? /*
           * A create carries the two lookup factors the check-in already collected [decision 0132]. They
           * are what KID-2 matches on, and until this landed nothing wrote them: the record was created,
           * the candidate came back, the four-factor lookup could not satisfy itself against two null
           * columns, and they were asked to fill in this page a second time. Nothing errored — which is
           * why it survived a full walkthrough.
           *
           * Spread ONLY on the create branch, deliberately. On an update the record was found by matching
           * on these very values, so re-sending them adds nothing and risks overwriting a good last-4 with
           * a digit somebody fumbled at check-in.
           */
          {
            ssnLast4: factors.ssnLast4,
            birthMonth: Number(factors.birthMonth),
            birthDay: Number(factors.birthDay),
          }
        : { candidateId }),
    });
    /*
     * ⛔ **A save that lands after Exit is dropped, whatever it says** [KID-1, KID-2: one visit, one person].
     * The candidate who pressed Continue may have pressed Exit and YES while this was in flight, and the
     * session open now is somebody else's: applying the answer wrote this person's candidate id into it and
     * took the next person to the openings as them. Dropping the answer changes nothing in Bullhorn: a
     * `saved` record stays created (0033's point), and a refusal or an outage had nothing left to tell
     * a candidate who has already gone.
     */
    if (!stillTheirs()) return;
    if (outcome.kind === 'rejected') {
      // A refusal from the api lands the candidate in the same place a local failure does: on the first
      // field the server named, not at the top of a form that looks unchanged.
      setRefused(rejectionErrors(outcome.fields));
      setRefusals((n) => n + 1);
      setNotSaved(true);
      setSaving(false);
      return;
    }
    if (outcome.kind === 'saved') setCandidateId(outcome.candidateId);
    goTo(SCREEN.jobs);
  }

  const errors = visibleErrors(draft, marked, refused);

  const err = (key: keyof ProfileDraft, message: string): string | undefined =>
    errors[key] === true ? t(message as Parameters<typeof t>[0]) : undefined;

  /*
   * A refusal REPLACES this screen without unmounting it, and that is the whole reason the gate lives in the
   * store while the draft stays here in `useState`.
   *
   * `Blocked · consent` promises, in ISS's own words, *"Nothing you typed has been lost."* Routing to a real
   * `Screen` would have unmounted this component and lost all eleven fields, so the promise would have been
   * false the moment the candidate took the way back the same frame draws. Returning early — after every
   * hook above has run — keeps the draft alive behind it.
   *
   * ONLY a declined consent has a way back, and it goes to the modal (*"go back and choose 'I agree'"*)
   * [0160; 0314]. A `no` to age or work authorisation ENDS the application [0161; ISS-201]: there is no
   * route back to this form, because the form is where the answer would be flipped and the create sent.
   * Their header Back asks to leave, as this form's own Back does; `Exit` ends the session.
   *
   * ⚠️ The draft still lives on behind a terminal refusal — the hooks above have all run — but nothing can
   * reach it again: nothing on this screen sets the gate back, and it leaves `blocked` only when the whole
   * candidate slice is replaced (`endCandidateSession`, `openCheckIn`) — the next visit, with an empty form.
   */
  if (gate?.stage === 'blocked') {
    return gate.reason === BLOCKED_REASON.consent ? (
      <BlockedScreen
        onExit={endCandidateSession}
        onGoBack={() => {
          setGate({ stage: 'consent' });
        }}
        reason={gate.reason}
      />
    ) : (
      <BlockedScreen onAskToExit={askToExit} onExit={endCandidateSession} reason={gate.reason} />
    );
  }

  return (
    <KioskScreen>
      {/*
       * NO journey rail here. The live frame (12021:40603 and its two siblings) draws Logo → Header →
       * Content with no Stepper at all, and neither does `11 · Returning profile`. The build showed one at
       * step 1, which pushed every field 68px down — a 44px rail plus the card's 24px gap — and made the
       * whole screen read as misaligned when only this one block was wrong.
       *
       * It is consistent with where the rail belongs: the five stages start once a job is in play, and this
       * screen is before that. Fit questions and Submitted DO draw it, and keep it.
       */}
      <KioskCard
        footer={
          /*
           * The action HUGS its label and sits right from `sm` up, and stretches at 375.
           *
           * Measured across the live frames on 2026-09-02: every one of these draws an auto-width primary
           * on the right — 122 on Application and Fit questions, 130 on See a rep, 224 on Returning profile,
           * 86 on Submitted — and 343 full-width at 375. The build shipped `fullWidth` everywhere, so at
           * desktop the button ran the whole 720px band. No jobs and the associate card keep `fullWidth`,
           * because their frames really do draw it full width; this is not a blanket rule.
           */
          <div className="flex w-full flex-col gap-4 sm:flex-row sm:items-center sm:justify-end">
            <Button disabled={saving} onClick={submit}>
              {t('application.continue')}
            </Button>
          </div>
        }
        header={
          <>
            {/*
              The Exit question, not `endCandidateSession` and never `goTo(SCREEN.welcome)` [ISS-149].
              Back out of the profile page leaves the visit, so it asks first — the same overlay the top
              bar's `Exit` opens — and NO keeps every field typed here. YES is `endCandidateSession`, and
              `goTo` would be wrong for the reason it always was: it carries no invariants by design, so
              it left the last-4 SSN, the birthday, the email and the matched id sitting in the store, on
              the ONE screen where the idle timer is deliberately disarmed because Welcome is meant to be
              the cleared state.
            */}
            <BackLink onClick={askToExit} />
            <StatusBadge label={t('badge.kiosk')} />
          </>
        }
      >
        <div className="flex w-full flex-col items-start gap-6 pb-8">
          {/*
            8px between the heading and its subtitle, not 4. The frames draw the heading block 84 tall on Fit
            questions (28 + 8 + 48) and 60 on Application (28 + 8 + 24); `gap-1` made both 4 short, which the
            gate reported as `Heading dh-4` and a 3px drift on everything beneath.
          */}
          <div className="flex w-full flex-col items-start gap-2">
            <h1
              className="kiosk-heading-focus w-full text-2xl font-bold leading-7 tracking-[-0.36px] text-black"
              ref={heading}
              tabIndex={-1}
            >
              {t('application.heading')}
            </h1>
            <p className="w-full text-sm leading-6 text-[var(--muted-foreground)]">
              {t('application.subtitle')}
            </p>
          </div>

          {/*
            EVERY placeholder below names its field — `t('application.city')`, never `Allentown` [0179].
            A specimen value is not a hint on THIS screen: the subtitle promises "Pre-filled from your
            lookup — edit if needed", so a field showing a plausible city reads as a claim about the
            candidate's record. ISS could not tell the two apart on the build, which is the finding 0179
            is written from — and it rejected swapping in a DIFFERENT specimen city, because the defect is
            the kind of string, not the city.

            0179 names City and ZIP; First name, Last name and Phone number follow by DERIVATION from its
            own general rule ("a copy sweep of every placeholder on every kiosk input"), which is why the
            fix is the label key and not five new strings — the placeholder and the label cannot drift
            apart, and ES arrives with EN.

            The two FORMAT hints stay: `e.g. 18.50` and `DateField`'s `MM/DD/YYYY` [0179 explicitly does
            not condemn them] — a rate format and a date format cannot be mistaken for the candidate's own
            record. `Email`'s `name@example.com` is read the same way, on the same test.

            ⏳ **What the sweep did NOT close, stated so it is findable:** those three are hardcoded English
            literals, so at `lang=es` this screen draws five Spanish placeholders and three English ones.
            That is a § 7 defect, not a 0179 one, and it is deliberately not fixed here: `checkIn.emailPlaceholder`
            already carries `nombre@ejemplo.com` and could simply be reused, but the other two need new
            Spanish copy, which is ISS's to approve [0040]. Reported to the Operator with the run.
          */}
          <Section title={t('application.personal')}>
            <Pair>
              <Field
                error={err('firstName', 'application.firstName.error')}
                id="application-firstName"
                placeholder={t('application.firstName')}
                required
                label={t('application.firstName')}
                onChange={(v) => {
                  set('firstName', v);
                }}
                value={draft.firstName}
              />
              <Field
                /*
                  No hint under this field. The live frame draws both name fields 64 tall with no helper
                  line, and because they share a flex row the hint stretched BOTH to 108 — 44px that then
                  pushed every section below it down. Figma decides the visual, and the affordance it paid
                  for (saying the surname came from the lookup) is already carried by the field arriving
                  filled in.
                */
                error={err('lastName', 'application.lastName.error')}
                hint={t('application.lastName.hint')}
                id="application-lastName"
                placeholder={t('application.lastName')}
                required
                label={t('application.lastName')}
                onChange={(v) => {
                  set('lastName', v);
                }}
                value={draft.lastName}
              />
            </Pair>
            <Pair>
              <Field
                error={err('email', 'application.email.error')}
                id="application-email"
                placeholder="name@example.com"
                required
                inputMode="email"
                label={t('application.email')}
                onChange={(v) => {
                  set('email', v);
                }}
                type="email"
                value={draft.email}
              />
              <Field
                error={err('phone', 'application.phone.error')}
                maxLength={10}
                id="application-phone"
                placeholder={t('application.phone')}
                required
                inputMode="numeric"
                label={t('application.phone')}
                onChange={(v) => {
                  // Digits only, as they type: Bullhorn stores what it is sent and its own form asks for
                  // ten digits with no spaces or symbols. Stripping here means a candidate who types
                  // (555) 123-4567 out of habit is not refused for it.
                  set('phone', digitsOnly(v));
                }}
                value={draft.phone}
              />
            </Pair>
          </Section>

          <Section title={t('application.location')}>
            <Field
              error={err('city', 'application.city.error')}
              id="application-city"
              placeholder={t('application.city')}
              required
              label={t('application.city')}
              onChange={(v) => {
                set('city', v);
              }}
              value={draft.city}
            />
            {/*
              316 and 120 with a 12px gutter, which is what the frame draws — 316 + 12 + 120 = the 448
              column exactly. It was 2:1 with a 16px gutter, giving 288/144: State 28px narrow and ZIP 24
              wide, on a select that is already the hardest control here to hit.
            */}
            {/*
              A ROW at every width, and ZIP is FIXED at 120 while State takes the rest — which is what the
              frame draws at both: 316 + 12 + 120 = 448 in the wide column, 195 + 12 + 120 = 327 at mobile.
              It was stacked below `sm` like the name pairs, but those really do stack there and this one
              does not; expressed as proportions it also needed two numbers where it needs none.
            */}
            <div className="flex w-full gap-3">
              <div className="min-w-0 flex-1">
                <Select
                  error={err('state', 'application.state.error')}
                  id="application-state"
                  required
                  label={t('application.state')}
                  onChange={(v) => {
                    set('state', v);
                  }}
                  options={US_STATES}
                  placeholder={t('application.state')}
                  value={draft.state}
                />
              </div>
              <div className="w-[120px] shrink-0">
                <Field
                  error={err('zip', 'application.zip.error')}
                  id="application-zip"
                  placeholder={t('application.zip')}
                  required
                  inputMode="numeric"
                  label={t('application.zip')}
                  maxLength={5}
                  onChange={(v) => {
                    set('zip', v);
                  }}
                  value={draft.zip}
                />
              </div>
            </div>
          </Section>

          <Section title={t('application.employment')}>
            <Field
              error={err('payRate', 'application.payRate.error')}
              hint={t('application.payRate.hint')}
              id="application-payRate"
              placeholder="e.g. 18.50"
              required
              inputMode="numeric"
              label={t('application.payRate')}
              onChange={(v) => {
                set('payRate', v);
              }}
              prefix="$"
              suffix="/hr"
              value={draft.payRate}
            />
            {/*
              `min` is TODAY, not tomorrow: ISS places and starts people the same day, so the floor has to
              include today [decision 0180, Heidi Carrel]. It stops the picker OFFERING an earlier day and
              nothing more — `validateProfile` still accepts a past date and so does the api, which is what
              keeps a returning candidate's stale value from blocking them. Do not mirror it in either.
            */}
            <DateField
              error={err('startDate', 'application.startDate.error')}
              id="application-startDate"
              required
              label={t('application.startDate')}
              min={todayIso()}
              onChange={(v) => {
                set('startDate', v);
              }}
              value={draft.startDate}
            />
            <YesNo
              error={err('workAuthorized', 'application.answerRequired')}
              required
              id="application-workAuthorized"
              onChange={(v) => {
                set('workAuthorized', v);
              }}
              question={t('application.workAuthorized')}
              value={draft.workAuthorized}
            />
            <YesNo
              error={err('isAdult', 'application.answerRequired')}
              required
              id="application-isAdult"
              onChange={(v) => {
                set('isAdult', v);
              }}
              question={t('application.isAdult')}
              value={draft.isAdult}
            />
          </Section>

          {/*
            NO consent block here any more [decision 0160, ISS — Heidi Carrel, 2026-09-03].

            It was a checkbox plus 384px of ISS's legal text inline on this screen, which is [0156]'s shape
            and the thing Heidi objected to: *"it's a lot of verbiage and having it on that screen and having
            it small and them scrolling through it seemed to be a lot."* 0156's actual holding survives — the
            arbitration clause is shown without the candidate having to act to reveal it — and it is now
            honoured in `ConsentDialog`, where the copy gets the whole screen. What 0156 measured (244px of
            this screen turning on the answer) is a trade-off that no longer exists.
          */}
          <Callout tone="warning">{t('application.sensitive')}</Callout>

          {/*
            No frame draws a save that failed, so this is the design system's own error treatment — the
            `--error` text a `Field` already shows — at screen level, rather than a new thing invented for
            it [convention skill § 8]. It renders only after a refusal, so the screen the frames measure is
            unchanged.

            It carries the refusals that mark NOTHING: `ssnLast4` and the birthday travel with a create and
            were typed at check-in, two screens back.
          */}
          {notSaved && (
            <p className="text-sm leading-5 text-[var(--error)]" role="alert">
              {t('application.saveRejected')}
            </p>
          )}
        </div>
      </KioskCard>

      {/*
        Rendered last so it paints over the card, and only while the gate is at the consent step. The dialog
        positions itself `fixed`, so where it sits in this tree decides nothing but the paint order.
      */}
      {gate?.stage === 'consent' && (
        <ConsentDialog
          onAnswer={(consented) => {
            // `answerConsent` owns its promise and never rejects: every failure becomes a value inside it.
            void answerConsent(consented);
          }}
        />
      )}
    </KioskScreen>
  );
}

/**
 * A titled group. The title in small uppercase above a **12px** stack.
 *
 * Measured off the live frame rather than assumed: the label sits at y=383 and is 16 tall, the first row
 * opens at 411 — a 12px gap — and the rows are 12 apart too, which is what makes the section 168 rather than
 * the 176 a 16px rhythm produced. Same 12 horizontally: two 218-wide fields in a 448 column leave exactly
 * that between them.
 */
function Section({
  title,
  children,
}: {
  readonly title: string;
  readonly children: React.ReactNode;
}) {
  return (
    <section className="flex w-full flex-col gap-3">
      {/*
        12/16 BOLD with 1.44px of tracking — the frame's own text node, read live 2026-09-08
        [12021:40616 `PERSONAL INFORMATION`: SF Pro Display Bold, 12px, 16 line-height, tracking 1.44px,
        `--muted-foreground`]. It was 600 at 0.6px, which is 0.84px per character narrower: over the
        twenty characters of the longest heading the label ran ~17px short of the 173px the frame draws,
        and read as a caption rather than as the divider between two sections of a long form.
      */}
      <h2 className="text-xs font-bold uppercase leading-4 tracking-[1.44px] text-[var(--muted-foreground)]">
        {title}
      </h2>
      {children}
    </section>
  );
}

/** Two controls side by side above 640px, stacked below — the frame's own arrangement at each width. */
function Pair({ children }: { readonly children: React.ReactNode }) {
  return <div className="flex w-full flex-col gap-3 sm:flex-row">{children}</div>;
}
